<template>
  <div
    ref="containerRef"
    class="bg-ld-background p-4 flex-1 flex flex-col overflow-hidden"
  >
    <!-- 标题栏 - 参考 CardDetailPopup.vue -->
    <div class="relative flex items-center h-24px mb-4">
      <span class="text-ld-label-secondary text-17px font-medium mx-auto">
        {{ _t('learn.edit_card') }}
      </span>
      <Icon
        name="close-circle"
        class="cursor-pointer w-25px absolute right-0"
        @click="emit('done', undefined)"
      />
    </div>

    <!-- 编辑内容区域 -->
    <div class="flex-1 overflow-auto">
      <CardPad
        :packageId="0"
        :cardDraft="cardDraft"
        :cards="[cardDraft]"
        :canDelete="true"
        :showFullEdit="false"
        :showDragHandle="false"
        :showMenu="true"
        :canCreate="false"
        :canCopy="false"
        @update="onCardUpdate"
        @delete="onDelete"
      />
    </div>

    <!-- 底部按钮 -->
    <div class="w-full mt-4 flex gap-2">
      <Button
        :label="_t('common.cancel')"
        scene="secondary"
        class="flex-1"
        @click="emit('done', undefined)"
      />
      <Button
        :label="_t('common.save')"
        class="flex-1"
        :loading="saving"
        @click="onSave"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, provide } from 'vue'
import {
  updateCard,
  deleteCardById,
  CardCreatedType,
  CardUpdatedType,
} from '@/api/package-source'
import type { Card } from '@/types/core'
import type { CardDraft } from '@/utils/card'
import CardPad from '@/components/package/CardPad/CardPad.vue'
import { validateCard } from '@/utils/card'

const props = defineProps<{
  card: Card
  cardId: number
  packageId?: number
}>()

const emit = defineEmits<{
  done: [result?: { type: 'update'; card: Card } | { type: 'delete' }]
}>()

const containerRef = ref<HTMLDivElement>()
const saving = ref(false)

provide('cardContainer', containerRef)

// 创建卡片草稿用于编辑
const cardDraft = reactive<CardDraft>({
  id: props.cardId,
  content: JSON.stringify(props.card),
  renderId: Date.now(),
  contentHash: '',
  createdType: CardCreatedType.NORMAL,
  updatedType: CardUpdatedType.NORMAL,
  createdAt: '',
  updatedAt: '',
  authorId: '',
})

let currentCard: Card = { ...props.card }

function onCardUpdate(newCard: Card) {
  currentCard = newCard
  cardDraft.content = JSON.stringify(newCard)
}

async function onSave() {
  // 校验卡片
  const validationError = validateCard(currentCard)
  if (validationError) {
    _message.info(validationError)
    return
  }

  saving.value = true

  try {
    const res = await updateCard(props.cardId, currentCard)
    if (res.code === 0) {
      _message.success(_t('common.save_success'))
      // 传递更新后的卡片数据给父组件
      emit('done', { type: 'update', card: currentCard })
    } else {
      _message.info(res.message)
    }
  } catch (_error) {
    _message.error(_t('common.save_failed'))
  } finally {
    saving.value = false
  }
}

async function onDelete() {
  // 显示确认对话框
  const confirmed = await _confirm({
    scene: 'warn',
    title: _t('common.delete_card'),
    content: _t('common.delete_tip'),
    primaryText: _t('common.cancel'),
    secondaryText: _t('common.delete'),
    confirmButton: 'secondary', // 指定删除按钮为确认按钮
  })

  if (!confirmed) {
    return
  }

  saving.value = true

  try {
    const res = await deleteCardById(props.packageId!, props.cardId)
    if (res.code === 0) {
      _message.success(_t('common.delete_success'))
      emit('done', { type: 'delete' })
    } else {
      _message.info(res.message)
    }
  } catch (_error) {
    _message.error(_t('common.delete_failed'))
  } finally {
    saving.value = false
  }
}
</script>

<style scoped></style>
